import { Link } from "react-router-dom";

import { Badge } from "../ui/Badge";
import { cn } from "../ui/cn";
import { relativeTime, shortDateTime } from "../../lib/time";
import { compactCurrency } from "../../lib/money";
import type {
  CompanyRow,
  CompanyWrite,
  CustomFieldDefinition,
  PicklistOption,
} from "../../lib/crm-types";
import { OwnerPicker, PicklistBadge, optionLabel } from "./FieldPickers";

/**
 * How each configurable column of the company list renders.
 *
 * A lookup rather than a switch inside the table so that adding a column is one
 * entry here and one in the API's `COMPANY_COLUMNS`, and so the settings editor
 * and the table can never disagree about what exists.
 *
 * Custom fields are not in this map: they are `cf:<id>` and all render the same
 * way, so the table handles them as the fallback.
 */
export interface ColumnContext {
  picklists: Record<string, PicklistOption[]>;
  /**
   * Save a change to one row, when the list is editable.
   *
   * Absent on a read-only rendering. Present, and the columns backed by a
   * dropdown become the dropdown -- which is the difference between working an
   * unmanaged-accounts queue in place and opening thirty companies in turn.
   */
  onPatch?: (row: CompanyRow, patch: Partial<CompanyWrite>) => void;
  /** Which row is mid-save, so its cell can dim rather than the whole table. */
  pendingId?: number | null;
  /**
   * The manager-defined fields, so a `cf:` column knows whether it is a
   * dropdown and what is in it.
   *
   * A column is only `cf:<id>` on its own -- the id says nothing about the type
   * -- so without these every custom field renders as plain text, including the
   * ones a manager built precisely so people could pick from a list.
   */
  customFields?: CustomFieldDefinition[];
  /** Custom fields have their own endpoint, so they need their own save. */
  onCustomPatch?: (
    row: CompanyRow,
    fieldId: number,
    value: string | null,
  ) => void;
}

type Renderer = (row: CompanyRow, context: ColumnContext) => React.ReactNode;

const dash = <span className="text-faint">—</span>;

/**
 * The picklist-backed columns, as one renderer each.
 *
 * `field` is the column on the company that the value is written to, and
 * `list` is the picklist it is chosen from -- the two names differ often enough
 * (`lifecycleStage` from `lifecycle_stage`) that pairing them here is what
 * stops a column writing to the wrong field.
 */
function picklistColumn(
  name: string,
  list: string,
  field: keyof CompanyWrite,
  read: (row: CompanyRow) => string | null,
): Renderer {
  return (row, { picklists, onPatch, pendingId }) => {
    const value = read(row);
    const options = picklists[list] ?? [];

    if (!onPatch) {
      return value ? optionLabel(options, value) : dash;
    }

    return (
      <PicklistBadge
        label={name}
        value={value}
        options={options}
        overlay
        pending={pendingId === row.id}
        onChange={(next) => onPatch(row, { [field]: next })}
      />
    );
  };
}

export const COLUMN_RENDERERS: Record<string, Renderer> = {
  account: (row) => row.tradeAccountNumber ?? dash,
  city: (row) => row.city ?? dash,
  region: (row) => row.region ?? dash,
  domain: (row) => row.domain ?? dash,

  /* A real tel: link, like the one on a sales call. Half this desk works from
     a softphone, and retyping a number off the screen is how you ring the
     wrong one. */
  phone: (row) =>
    row.phone ? (
      <a
        href={`tel:${row.phone}`}
        className="text-brand-600 hover:underline"
        onClick={(event) => event.stopPropagation()}
      >
        {row.phone}
      </a>
    ) : (
      dash
    ),

  stage: (row, context) => {
    const value = row.lifecycleStage;
    const options = context.picklists.lifecycle_stage ?? [];

    /* Read-only keeps the badge, which is what makes a stage scannable down
       the column; the editable one brings its own. */
    if (!context.onPatch) {
      return value ? <Badge>{optionLabel(options, value)}</Badge> : dash;
    }

    return picklistColumn(
      "stage",
      "lifecycle_stage",
      "lifecycleStage",
      (r) => r.lifecycleStage,
    )(row, context);
  },

  type: picklistColumn(
    "type",
    "customer_type",
    "customerType",
    (row) => row.customerType,
  ),

  priority: picklistColumn(
    "priority",
    "lead_priority",
    "leadPriority",
    (row) => row.leadPriority,
  ),

  source: picklistColumn(
    "source",
    "lead_source",
    "leadSource",
    (row) => row.leadSource,
  ),

  /* An unmanaged account is the one value in this table worth colouring: it is
     a worklist, and this is the column that clears it. */
  owner: (row, { onPatch, pendingId }) => {
    const name = row.ownerName;

    if (!onPatch) {
      return name ?? <span className="text-sla-warning">Unmanaged</span>;
    }

    return (
      <OwnerPicker
        ownerUserId={row.ownerUserId}
        overlay
        triggerLabel={name ?? "Unmanaged"}
        triggerClassName={name ? "text-muted" : "text-sla-warning"}
        pending={pendingId === row.id}
        onChange={(ownerUserId) => onPatch(row, { ownerUserId })}
      />
    );
  },

  people: (row) => <span className="tabular-nums">{row.contactCount}</span>,

  branches: (row) =>
    row.branchCount > 0 ? (
      <Link
        to={`/crm/companies?parent=${row.id}`}
        className="tabular-nums hover:text-brand-600"
      >
        {row.branchCount}
      </Link>
    ) : (
      dash
    ),

  team: (row) => row.external?.id ?? dash,
  vat: (row) => row.vatNumber ?? dash,
  companyNumber: (row) => row.companyNumber ?? dash,

  /*
   * The credit columns.
   *
   * Every one of them is a dash for a company nobody has linked to Creditsafe,
   * and that is the honest rendering: it means "we have not looked this up",
   * which is a different thing from a company with no money and must not read
   * as one. Nothing here is computed -- see `FinancialsPanel` for the same rule
   * stated at length.
   */
  creditRating: (row) => <CreditRating value={row.creditRating} />,
  creditScore: (row) =>
    row.creditScore === null ? (
      dash
    ) : (
      <span className="tabular-nums" title="Creditsafe's local score, out of 100">
        {row.creditScore}
      </span>
    ),
  creditLimit: (row) => money(row.creditLimit),
  turnover: (row) => money(row.turnover),
  shareholdersFunds: (row) => money(row.shareholdersFunds),

  /**
   * When anybody last did anything with this account.
   *
   * The newest of a ticket message, a sales call, a meeting or a CRM activity
   * -- not `updatedAt`, which moves when a rep corrects a postcode. Null is
   * meaningful and says so: an account nobody has ever contacted is a
   * different thing from one last touched a long time ago, and rendering it as
   * a dash beside real dates loses that.
   */
  lastActivity: (row) =>
    row.lastActivityAt ? (
      <span title={shortDateTime(row.lastActivityAt)}>
        {relativeTime(row.lastActivityAt)}
      </span>
    ) : (
      <span className="text-faint">Never</span>
    ),

  updated: (row) => shortDateTime(row.updatedAt),
};

/**
 * Whole pounds, grouped, or a dash.
 *
 * Pounds because that is what the `companies` money columns hold -- they carry
 * no currency of their own, and every account on this desk is registered in
 * Britain. The company page prefers the linked report's own currency where it
 * has one; a list cannot, since it would mean loading a report per row.
 */
function money(value: number | null): React.ReactNode {
  if (typeof value !== "number" || !Number.isFinite(value)) return dash;

  return <span className="tabular-nums">{compactCurrency(value)}</span>;
}

/**
 * The standardised A–E band, as a badge.
 *
 * Coloured so a list of two hundred accounts can be scanned for the bad ones,
 * which is most of why anybody would turn this column on.
 *
 * **E is deliberately not red.** Creditsafe's common rating runs A (very low
 * risk) to D (high risk), and uses E for a company it has *not scored* -- a
 * young company, a sole trader, one it holds too little on. Painting that red
 * would report "high risk" where the bureau's answer is "no opinion", and a rep
 * would decline an account on the strength of it. Grey says unknown, which is
 * what it means.
 */
function CreditRating({ value }: { value: string | null }) {
  if (!value) return dash;

  const band = value.trim().toUpperCase();

  const tone =
    band === "A" || band === "B"
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
      : band === "C"
        ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
        : band === "D"
          ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
          : "bg-surface-2 text-muted-on-tint";

  return (
    <span
      title={
        band === "E"
          ? "Creditsafe has not scored this company — not the same as a poor score"
          : "Creditsafe's standardised rating, A (lowest risk) to D (highest)"
      }
      className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", tone)}
    >
      {band}
    </span>
  );
}

/** A manager-defined field, which is `cf:<id>` and always plain text. */
export function renderColumn(
  key: string,
  row: CompanyRow,
  context: ColumnContext,
): React.ReactNode {
  const renderer = COLUMN_RENDERERS[key];
  if (renderer) return renderer(row, context);

  if (key.startsWith("cf:")) {
    const fieldId = key.slice(3);
    const value = row.customValues[fieldId] ?? null;
    const field = context.customFields?.find(
      (definition) => String(definition.id) === fieldId,
    );

    /*
     * A dropdown gets the same picker the built-in fields get. "Customer Type"
     * on this desk is a custom field rather than the built-in one, so without
     * this the field somebody most expects to click is the one that does
     * nothing -- which is exactly how it was reported.
     */
    if (
      context.onCustomPatch &&
      field?.type === "dropdown" &&
      field.options !== null
    ) {
      return (
        <PicklistBadge
          label={field.label.toLowerCase()}
          value={value}
          options={field.options.map((option) => ({
            value: option,
            label: option,
          }))}
          overlay
          pending={context.pendingId === row.id}
          onChange={(next) => context.onCustomPatch!(row, field.id, next)}
        />
      );
    }

    return value ?? dash;
  }

  return dash;
}

/**
 * Columns that only earn their width on a wide screen.
 *
 * The first two are always visible because a company with no account number and
 * no location is hard to tell from another of the same name.
 */
export function columnVisibility(index: number): string {
  if (index === 0) return "";
  if (index === 1) return "hidden sm:table-cell";
  if (index <= 3) return "hidden md:table-cell";
  return "hidden lg:table-cell";
}

/**
 * How wide each column starts, in pixels.
 *
 * Sized to the value rather than to the heading. Left to itself the table hands
 * every column an equal share of whatever is spare, so "Credit rating" -- one
 * letter -- came out as wide as an account manager's name, and the eye has to
 * cross an inch of nothing to get from the badge to the next figure.
 *
 * A starting point, not a rule: anything here can be dragged, and what somebody
 * drags is remembered (see `useColumnWidths`).
 */
const COLUMN_WIDTHS: Record<string, number> = {
  account: 110,
  city: 120,
  region: 110,
  domain: 170,
  phone: 130,
  stage: 120,
  type: 120,
  priority: 110,
  source: 120,
  owner: 150,
  people: 72,
  branches: 80,
  team: 80,
  vat: 120,
  companyNumber: 120,
  creditRating: 96,
  creditScore: 96,
  creditLimit: 110,
  turnover: 110,
  shareholdersFunds: 140,
  lastActivity: 110,
  updated: 120,
};

/** A custom field could hold anything, so it gets a middling default. */
export function defaultColumnWidth(key: string): number {
  return COLUMN_WIDTHS[key] ?? 120;
}

/** The money columns, which are the only ones worth right-aligning. */
const RIGHT_ALIGNED = new Set(["creditLimit", "turnover", "shareholdersFunds"]);

/**
 * How a column is aligned, applied to both the header and the cells.
 *
 * Only the money columns move. Ranging three currency figures on their last
 * digit is what makes them comparable down a column -- left-aligned, "£45.0K"
 * above "£12.3M" reads as the larger of the two. Deliberately not applied to
 * the counts (`people`, `branches`) or the score: they are one or two
 * characters wide, so alignment buys nothing and changing them would move
 * columns that have looked the same since the list shipped.
 */
export function columnAlignment(key: string): string {
  return RIGHT_ALIGNED.has(key) ? "text-right" : "";
}
